import React from 'react';
import IdeaNode from './IdeaNode';
import { Idea } from '../types';
import { FaSpinner, FaFileAlt, FaPencilAlt, FaNewspaper, FaDesktop, FaBook } from 'react-icons/fa';
import { Tooltip } from 'react-tooltip';
import './Project.css';

interface SelectedIdeasProps {
  ideas: Idea[];
  selectedIdeas: Set<string>;
  onExpand: (idea: Idea) => void;
  onAddSimilar: (idea: Idea) => void;
  onSelect: (idea: Idea) => void;
  onCollapseChildren: (idea: Idea) => void;
  onDelete: (idea: Idea) => void;
  onRegenerate: (idea: Idea) => void;
  onRewrite: (idea: Idea) => void;
  expandedIdeas: Set<string>;
  expandingIdea: string | null;
  generatingSimilar: string | null;
  rewritingIdea: string | null;
  deletingIdea: string | null;
  regeneratingIdea: string | null;
  onGenerateArtifact: (type: string) => void;
  generatingArtifact: string | null;
  filterSelectedIdeasWithParents: (ideas: Idea[]) => Idea[];
}

const SelectedIdeas: React.FC<SelectedIdeasProps> = ({
  ideas,
  selectedIdeas,
  onExpand,
  onAddSimilar,
  onSelect,
  onCollapseChildren,
  onDelete,
  onRegenerate,
  onRewrite,
  expandedIdeas,
  expandingIdea,
  generatingSimilar,
  rewritingIdea,
  deletingIdea,
  regeneratingIdea,
  onGenerateArtifact,
  generatingArtifact,
  filterSelectedIdeasWithParents,
}) => {
  return (
    <div className="ideas-list">
      {selectedIdeas.size > 0 && (
        <div className="selected-toolbar">
          <button
            className="text-button"
            onClick={() => onGenerateArtifact('prfaq')}
            disabled={generatingArtifact === 'prfaq'}
            data-tooltip-id="artifact-tooltip"
            data-tooltip-content="Generate Amazon-style PR FAQ document from selected ideas"
          >
            {generatingArtifact === 'prfaq' ? (
              <><FaSpinner className="spinner" /> Generating...</>
            ) : (
              <>
                <FaFileAlt style={{ marginRight: '8px' }} />
                PR FAQ
              </>
            )}
          </button>
          <button
            className="text-button"
            onClick={() => onGenerateArtifact('prd')}
            disabled={generatingArtifact === 'prd'}
            data-tooltip-id="artifact-tooltip"
            data-tooltip-content="Create a detailed Product Requirements Document"
          >
            {generatingArtifact === 'prd' ? (
              <><FaSpinner className="spinner" /> Generating...</>
            ) : (
              <>
                <FaPencilAlt style={{ marginRight: '8px' }} />
                PRD
              </>
            )}
          </button>
          <button
            className="text-button"
            onClick={() => onGenerateArtifact('blog')}
            disabled={generatingArtifact === 'blog'}
            data-tooltip-id="artifact-tooltip"
            data-tooltip-content="Convert ideas into an engaging blog post"
          >
            {generatingArtifact === 'blog' ? (
              <><FaSpinner className="spinner" /> Generating...</>
            ) : (
              <>
                <FaNewspaper style={{ marginRight: '8px' }} />
                Blog Post
              </>
            )}
          </button>
          <button
            className="text-button"
            onClick={() => onGenerateArtifact('presentation')}
            disabled={generatingArtifact === 'presentation'}
            data-tooltip-id="artifact-tooltip"
            data-tooltip-content="Create a presentation deck from selected ideas"
          >
            {generatingArtifact === 'presentation' ? (
              <><FaSpinner className="spinner" /> Generating...</>
            ) : (
              <>
                <FaDesktop style={{ marginRight: '8px' }} />
                Presentation
              </>
            )}
          </button>
          <button
            className="text-button"
            onClick={() => onGenerateArtifact('research')}
            disabled={generatingArtifact === 'research'}
            data-tooltip-id="artifact-tooltip"
            data-tooltip-content="Generate a research paper with citations and methodology"
          >
            {generatingArtifact === 'research' ? (
              <><FaSpinner className="spinner" /> Generating...</>
            ) : (
              <>
                <FaBook style={{ marginRight: '8px' }} />
                Research Paper
              </>
            )}
          </button>
          <Tooltip
            id="artifact-tooltip"
            place="top"
            className="tooltip"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              padding: '8px 12px',
              borderRadius: '6px',
              fontSize: '0.875rem',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              maxWidth: '220px'
            }}
          />
        </div>
      )}
      {filterSelectedIdeasWithParents(ideas).map((idea, index) => (
        <IdeaNode
          key={idea.id}
          idea={idea}
          index={index + 1}
          onExpand={onExpand}
          onAddSimilar={onAddSimilar}
          onSelect={onSelect}
          onCollapseChildren={onCollapseChildren}
          onDelete={onDelete}
          onRegenerate={onRegenerate}
          expandedIdeas={expandedIdeas}
          expandingIdea={expandingIdea}
          selectedIdeas={selectedIdeas}
          generatingSimilar={generatingSimilar}
          onRewrite={onRewrite}
          rewritingIdea={rewritingIdea}
          deletingIdea={deletingIdea}
          regeneratingIdea={regeneratingIdea}
        />
      ))}
    </div>
  );
};

export default SelectedIdeas;
